/**
 * **A block** — booking-system R4: one absolute stretch of time the trainer is not bookable in,
 * visible to the trainer only. The „Nowa blokada" dialog (T-B2b) authors one.
 *
 * The dialog asks in the trainer's own words — a day and an hour, on their clock — and the wire
 * takes two INSTANTS (`PostBlockRequest.StartsAt`/`EndsAt`, refused when they carry no zone:
 * `block_not_an_instant`). The conversion happens once, here, through `zonedInstant`, and both ends
 * travel as UTC.
 *
 * What the server refuses, reached first so „Zapisz blokadę" says why instead of sending
 * (`AvailabilityProcessor.AddBlockAsync`): an end not after its start (`block_range_invalid`), an end
 * or a start outside the calendar's window — no earlier than a day ago, no later than 730 days ahead
 * (`CalendarDateBounds`, `availability_range_invalid`) — and a note over 500 characters (the DTO's
 * `MaxLength`). The far end is held STRICTER than the server's 730 days: a block ends no later than
 * the close of the last day the Grafik tab reads (`lastStatableDay`), so a saved block never falls
 * off the list that shows it. A block never touches sessions already standing (R4), which the dialog says.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { TrainWithMeModulesBookingDtosRequestsPostBlockRequest as PostBlockRequest } from '~/shared/api/generated/model';

import { lastStatableDay } from './dateException';
import { shiftDay, wallReading, zonedInstant, type CivilDay } from './trainerClock';

/** `PostBlockRequest.Note`'s `[MaxLength(500)]`. */
export const MAX_BLOCK_NOTE = 500;

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;
/** `CalendarDateBounds`: a day of slack behind. */
const PAST_SLACK_MS = DAY_MS;

export interface BlockDraft {
  readonly fromDay: CivilDay;
  /** Minutes since the day's midnight, on the trainer's clock. */
  readonly fromMinutes: number;
  readonly toDay: CivilDay;
  readonly toMinutes: number;
  readonly note: string;
}

export type BlockProblem = 'rangeInvalid' | 'startTooEarly' | 'tooFar' | 'noteTooLong';

/**
 * A fresh block: the next whole hour on the trainer's clock, for two hours. A block is most often
 * „this afternoon" or „this weekend", and a form that opens on something plausible is edited
 * rather than filled in.
 */
export function blockDraftFrom(nowMs: number, zone: string): BlockDraft {
  const startMs = Math.ceil((nowMs + 1) / HOUR_MS) * HOUR_MS;
  const from = wallReading(startMs, zone);
  const to = wallReading(startMs + 2 * HOUR_MS, zone);
  return {
    fromDay: from.day,
    fromMinutes: from.minutes,
    toDay: to.day,
    toMinutes: to.minutes,
    note: '',
  };
}

/** The two ends as instants. */
export function blockSpan(
  draft: BlockDraft,
  zone: string,
): { readonly startMs: number; readonly endMs: number } {
  return {
    startMs: zonedInstant(draft.fromDay, draft.fromMinutes, zone),
    endMs: zonedInstant(draft.toDay, draft.toMinutes, zone),
  };
}

/** What stops „Zapisz blokadę", in the order the trainer would fix it — or `null`. */
export function blockProblem(draft: BlockDraft, nowMs: number, zone: string): BlockProblem | null {
  const { startMs, endMs } = blockSpan(draft, zone);
  if (endMs <= startMs) return 'rangeInvalid';
  if (startMs < nowMs - PAST_SLACK_MS) return 'startTooEarly';
  const lastDay = lastStatableDay(wallReading(nowMs, zone).day);
  if (endMs > zonedInstant(shiftDay(lastDay, 1), 0, zone)) return 'tooFar';
  if (draft.note.trim().length > MAX_BLOCK_NOTE) return 'noteTooLong';
  return null;
}

/** `POST /trainer-availability/block`'s body: two UTC instants, and the note or nothing. */
export function blockRequest(draft: BlockDraft, zone: string): PostBlockRequest {
  const { startMs, endMs } = blockSpan(draft, zone);
  const note = draft.note.trim();
  return {
    startsAt: new Date(startMs).toISOString(),
    endsAt: new Date(endMs).toISOString(),
    note: note === '' ? null : note,
  };
}
